import { useEffect, useState } from 'react';
import { useChatContext } from 'stream-chat-react';
import type { MessageFilters, SearchOptions, MessageResponse } from 'stream-chat';

type UseChannelSearchParams<T> = {
  filter: MessageFilters;
  searchOptions?: SearchOptions;
  transform: (messages: MessageResponse[]) => T;
  initialData: T;
};

export function useChannelContentSearch<T>({
  filter,
  searchOptions,
  transform,
  initialData,
}: UseChannelSearchParams<T>) {
  const { channel } = useChatContext();

  const [data, setData] = useState<T>(initialData);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!channel) return;

    setData(initialData);
    setIsLoading(true);

    const fetchData = async () => {
      try {
        const result = await channel.search(filter, searchOptions);
        setData(transform(result.results.map(({ message }) => message)));
      } catch {
        setData(initialData);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channel?.id]);

  return { data, isLoading };
}
